import { useLicenseDetails } from "../../contexts/licenseDetails";
import Button from "../Button/Button";
import { useEffect, useState } from "react";
import Input from "../Input/Input";
import { BillingPeriodSelector } from "../BillingPeriodSelector/BillingPeriodSelector";
import { PriceInput } from "../PriceInput/PriceInput";
import { DiscountReasonSelector } from "../DiscountReasonSelector/DiscountReasonSelector";
import { formatMoney, parseMoney } from "../../utils/number";
import LicenseService from "../../services/LicensesService";
import {
   getZodFieldErrors,
   licenseBillingSchema,
} from "../../validations/license-subpages.schema";

export function LicenseCreateBillingPage({ license, ...props }) {
   const { setCurrentSubTab, setLicense } = useLicenseDetails();

   const [period, setPeriod] = useState("");
   const [amount, setAmount] = useState(0);
   const [dueDate, setDueDate] = useState("");
   const [isRecalculatingTotalAmount, setIsRecalculatingTotalAmount] = useState(false);
   const [totalAmount, setTotalAmount] = useState(0);
   const [totalAmountError, setTotalAmountError] = useState("");
   const [hasCalculatedTotal, setHasCalculatedTotal] = useState(false);
   const [discountReason, setDiscountReason] = useState(null);
   const [errors, setErrors] = useState({});

   const [isSubmitting, setIsSubmitting] = useState(false);

   useEffect(() => {
      setPeriod(license?.currentBilling?.period ?? "");
      setAmount(Number(license?.currentBilling?.amount ?? 0));
      setDueDate(
         (license?.nextBilling?.dueDate ?? license?.currentBilling?.dueDate ?? "")
            .toString()
            .slice(0, 10)
      );
   }, [license]);

   async function handleSubmit(event) {
      event.preventDefault();
      const validation = licenseBillingSchema.safeParse({
         period,
         dueDate,
         amount,
         totalAmount,
         hasCalculatedTotal:
            hasCalculatedTotal && !isRecalculatingTotalAmount && !totalAmountError,
         discountReason,
      });
      if (!validation.success) {
         setErrors(getZodFieldErrors(validation.error));
         return;
      }
      setErrors({});
      const hasDiscount = amount < totalAmount;

      setIsSubmitting(true);
      try {
         const response = await LicenseService.createBilling({
            licenseId: license.id,
            dueDate: new Date(`${dueDate}T12:00:00`).toISOString(),
            amount: hasDiscount ? totalAmount : amount,
            period,
            ...(hasDiscount && {
               discount: {
                  amount: totalAmount - amount,
                  type: "FIXED",
                  reason: discountReason.trim(),
               },
            }),
         });
         if (response?.status >= 200 && response.status < 300) {
            const refreshedLicense = await LicenseService.getLicense(license.id);
            if (refreshedLicense?.status >= 200 && refreshedLicense.status < 300) {
               setLicense(refreshedLicense.data);
            }
            setCurrentSubTab(null);
         }
      } finally {
         setIsSubmitting(false);
      }
   }

   async function recalculateTotalAmount() {
      if (!license?.type?.code || !period) {
         setTotalAmount(0);
         setHasCalculatedTotal(false);
         setTotalAmountError("");
         return;
      }

      setIsRecalculatingTotalAmount(true);
      setHasCalculatedTotal(false);
      setTotalAmountError("");
      try {
         const response = await LicenseService.calculateTotalAmount(
            period,
            license.type.code,
            license.features?.map(f => f.code)
         );

         const calculatedAmount = Number(response?.data?.totalAmount);
         if (
            response?.status < 200 ||
            response?.status >= 300 ||
            !Number.isFinite(calculatedAmount)
         ) {
            throw new Error(
               response?.data?.message ?? "Não foi possível calcular o valor da cobrança."
            );
         }
         setTotalAmount(calculatedAmount);
         setHasCalculatedTotal(true);
      } catch (error) {
         console.error("Erro ao calcular o valor total:", error);
         setTotalAmount(0);
         setTotalAmountError(error.message);
      } finally {
         setIsRecalculatingTotalAmount(false);
      }
   }

   useEffect(() => {
      recalculateTotalAmount();
   }, [
      period
   ]);

   return (
      <div className="fade-in mt-0.5!">
         <div className="flex flex-row items-center justify-between">
            <h2 className="font-bold! text-[15px]!">Nova cobrança</h2>
         </div>
         <p className="mt-3! opacity-60 leading-snug!">
            Adicione uma nova cobrança. Utilize essa funcionalidade para alterar também o ciclo atual e valores.
         </p>
         <form
            className="flex flex-col gap-3 mt-7!"
            onSubmit={handleSubmit}
         >
            <BillingPeriodSelector
               value={period}
               onChange={({ target }) => {
                  setPeriod(target.value)
               }}
               isSubmitting={isSubmitting}
               error={errors.period}
            />
            <Input
               type="date"
               name="dueDate"
               id="billing-due-date"
               labelText="Data de vencimento *"
               value={dueDate}
               onChange={({ target }) => setDueDate(target.value)}
               disabled={isSubmitting}
               error={errors.dueDate}
            />
            <div className="w-full flex-none! grid grid-cols-2 gap-3!">
               <PriceInput
                  name="amount"
                  id="amount"
                  labelText="Valor *"
                  value={formatMoney(amount ?? 0)}
                  onChange={({ target }) => {
                     setAmount(Number(target.value) || 0);
                  }}
                  error={errors.amount}
                  disabled={isSubmitting}
               />
               <div className="w-full mt-5! bg-(--borderColor)/40! rounded-(--borderRadius)! box-border! px-3! py-2! text-(--textColor) flex items-center justify-between gap-3!">
                  <span>Total previsto: </span>
                  <b className="font-semibold!">
                     {isRecalculatingTotalAmount ? (
                        <>Recalculando...</>
                     ) : (
                        <>
                           R$ {formatMoney(totalAmount)}
                           {amount !== totalAmount && (
                              <span className={`${totalAmount - amount < 0 ? 'text-green-500' : 'text-red-400'} ml-2! text-xs!`}>
                                 ({totalAmount - amount < 0 ? '+' : '-'}{formatMoney(Math.abs(totalAmount - amount))})
                              </span>
                           )}
                        </>
                     )}
                  </b>
               </div>
               {totalAmountError && (
                  <span className="col-span-2 error-message">{totalAmountError}</span>
               )}
               {amount < totalAmount && (
                  <div className="col-span-2">
                     <DiscountReasonSelector
                        value={discountReason}
                        setValue={setDiscountReason}
                        type={props.type}
                        error={errors.discountReason}
                        disabled={props.type === "consulta"}
                     />
                  </div>
               )}
            </div>

            <div className="w-full flex flex-row items-center justify-end gap-3! mt-6!">
               <Button
                  className={"button-white"}
                  label={"Cancelar"}
                  onClick={() => setCurrentSubTab(null)}
                  disabled={isSubmitting}
               />
               <Button
                  className={"button-blue"}
                  disabled={isSubmitting || props.type === "consulta"}
                  label={"Salvar"}
                  type={"submit"}
                  isLoading={isSubmitting}
               />
            </div>
         </form>
      </div>
   );
}